import React, { FC, useMemo } from 'react';

import { Divider } from '@rbilabs/universal-components';
import { useCartWithServerOrderSurcharges } from 'features/cart/hooks/use-cart-with-server-order-surcharges';
import { useFeatureDisclaimers } from 'features/content/hooks/use-feature-disclaimers';
import { diagnosticMoney } from 'features/diagnostics/context/projectors';
import {
  useDiagnosticContributor,
  useDiagnosticToolsEnabled,
} from 'features/diagnostics/context/provider';
import { StyleSheet } from 'react-native';

import { IBackendCart, ILegacyCartEntry } from '@rbi-ctg/menu';
import Picture from 'components/picture';
import { Box } from 'components/ui/box';
import { DeliveryStatus } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { useCartContext } from 'state/cart';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { useServiceModeLive } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { spacing } from 'styles/constants';
import { computeDeliveryFee } from 'utils/cart/helper';
import priceForCartEntry from 'utils/menu/price-for-cart-entry';
import {
  geographicalFeeLabelIdMap,
  isMappedGeographicalFeeState,
} from 'utils/order/geographical-fee';

import DonationsRow from './donations-row';
import { FeeRows } from './fee-rows';
import GrandTotal from './grand-total';
import SubTotal from './subtotal';
import TaxesAndFees from './taxes-and-fees';
import { TaxesAndFeesNextStep } from './taxes-and-fees-next-step';
import { TaxesRow } from './taxes-row';
import { ITotals } from './types';
import { useTotalTipCalculations } from './use-total-tip-calculations';
import { getFeesByGroup, getStandardizedName } from './utils';

const Separator = Divider.withConfig({
  marginX: 0,
  marginTop: '$4',
  thickness: '1',
});

export const Totals: FC<React.PropsWithChildren<ITotals>> = ({
  serverOrder,
  shouldSkipPriceOrder,
  isLoading,
}) => {
  const { calculateCartTotalWithDiscount, cartEntries = [] } = useCart();
  const serviceMode = useServiceModeLive();

  const {
    subTotalCents = 0,
    taxCents = 0,
    rewardDiscountAmount,
    serverOrderTip,
    additionalDiscountAmount,
    deliveryFee,
    deliveryFeeDiscount,
    cart,
    delivery,
    isDeliveryOrder,
  } = useTotalTipCalculations({ serverOrder });

  const { featureDisclaimers } = useFeatureDisclaimers();
  const { deliveryLogo } = featureDisclaimers || {};
  const { cartTotal = 0, isCartTotalNegative = false } = calculateCartTotalWithDiscount();

  const isSignUpAfterCart = useIsSignUpAfterCartEnabled();
  const { cart: cartWithServerOrderSurcharges } = useCartWithServerOrderSurcharges(cart);

  const { selectedDonationCartEntry } = useCartContext();
  const donationAmount = useMemo(() => selectedDonationCartEntry?.price || 0, [
    selectedDonationCartEntry,
  ]);

  const showItemDonationRow = donationAmount > 0;
  // If we cannot use a priced server cart yet, keep showing local cart preview values.
  const skipOrderPricing =
    isSignUpAfterCart ||
    isCartTotalNegative ||
    shouldSkipPriceOrder ||
    !cartWithServerOrderSurcharges;
  const cartSelected = skipOrderPricing
    ? ({
        totalCents: cartTotal,
        subTotalCents: cartTotal,
      } as IBackendCart)
    : cartWithServerOrderSurcharges;

  const deliveryLoading = serverOrder?.delivery?.status === DeliveryStatus.QUOTE_REQUESTED;
  const dropoffState = serverOrder?.delivery?.dropoff?.state;
  const otherDiscountAmount = isCartTotalNegative ? 0 : additionalDiscountAmount;
  const rewardDiscount = isCartTotalNegative ? 0 : rewardDiscountAmount;

  return (
    <Box style={styles.wrapper}>
      <LegacyCartDisplayDiagnosticContributor
        cartEntries={cartEntries}
        deliveryFee={deliveryFee ?? 0}
        deliveryFeeDiscount={deliveryFeeDiscount ?? 0}
        displayedSubtotal={Math.max(cartSelected?.subTotalCents ?? 0, 0)}
        displayedTotal={Math.max((cartSelected?.totalCents ?? 0) - Number(serverOrderTip), 0)}
        donationAmount={donationAmount}
        dropoffState={dropoffState}
        fees={cartSelected?.fees}
        geographicalFeeCents={delivery?.geographicalFeeCents ?? 0}
        isDeliveryOrder={Boolean(isDeliveryOrder)}
        isLoading={isLoading}
        otherDiscountAmount={otherDiscountAmount}
        rewardDiscount={rewardDiscount ?? 0}
        serviceFeeCents={delivery?.serviceFeeCents ?? 0}
        shouldShowDonation={showItemDonationRow}
        skipOrderPricing={Boolean(skipOrderPricing)}
        smallCartFeeCents={delivery?.smallCartFeeCents ?? 0}
        taxCents={taxCents}
      />
      <Box>
        {showItemDonationRow ? (
          <Box style={styles.donationRow}>
            <DonationsRow
              isLoading={isLoading && !skipOrderPricing}
              totalCents={donationAmount}
              message={t('donation')}
              textProps={{ fontSize: 'md' }}
            />
          </Box>
        ) : null}

        <SubTotal
          cart={cartSelected}
          isLoading={isLoading && !skipOrderPricing}
          otherDiscountAmount={otherDiscountAmount}
          rewardDiscount={rewardDiscount}
        />

        <FeeRows
          fees={cartSelected.fees}
          isLoading={isLoading && !skipOrderPricing}
          textProps={{ fontSize: 'md' }}
        />

        {skipOrderPricing ? (
          <TaxesAndFeesNextStep textProps={{ fontSize: 'md' }} />
        ) : isDeliveryOrder ? (
          <TaxesAndFees
            deliveryFee={deliveryFee}
            deliveryFeeDiscount={deliveryFeeDiscount}
            dropoffState={dropoffState}
            serviceFeeCents={delivery?.serviceFeeCents ?? 0}
            smallCartFeeCents={delivery?.smallCartFeeCents ?? 0}
            geographicalFeeCents={delivery?.geographicalFeeCents ?? 0}
            taxCents={taxCents}
            serviceMode={serviceMode}
            subTotalCents={subTotalCents}
            isLoading={isLoading || deliveryLoading}
          />
        ) : (
          <TaxesRow textProps={{ fontSize: 'md' }} taxCents={taxCents} isLoading={isLoading} />
        )}

        <Separator />

        <GrandTotal
          cart={cartSelected}
          isLoading={isLoading && !skipOrderPricing}
          serverOrderTip={serverOrderTip}
          tipAmount={0}
        />
      </Box>
      {isDeliveryOrder && deliveryLogo?.locale ? (
        <Box style={styles.deliveryLogoContainer}>
          <Picture image={deliveryLogo?.locale} alt={t('deliveryLogoAlt')} />
        </Box>
      ) : null}
    </Box>
  );
};

interface LegacyCartDisplayDiagnosticContributorProps {
  cartEntries: ILegacyCartEntry[];
  deliveryFee: number;
  deliveryFeeDiscount: number;
  displayedSubtotal: number;
  displayedTotal: number;
  donationAmount: number;
  dropoffState?: string | null;
  fees?: IBackendCart['fees'];
  geographicalFeeCents: number;
  isDeliveryOrder: boolean;
  isLoading: boolean;
  otherDiscountAmount: number;
  rewardDiscount: number;
  serviceFeeCents: number;
  shouldShowDonation: boolean;
  skipOrderPricing: boolean;
  smallCartFeeCents: number;
  taxCents: number;
}

const LegacyCartDisplayDiagnosticContributor = (
  props: LegacyCartDisplayDiagnosticContributorProps
) => {
  const enabled = useDiagnosticToolsEnabled();

  return enabled ? <EnabledLegacyCartDisplayDiagnosticContributor {...props} /> : null;
};

const EnabledLegacyCartDisplayDiagnosticContributor = ({
  cartEntries,
  deliveryFee,
  deliveryFeeDiscount,
  displayedSubtotal,
  displayedTotal,
  donationAmount,
  dropoffState,
  fees,
  geographicalFeeCents,
  isDeliveryOrder,
  isLoading,
  otherDiscountAmount,
  rewardDiscount,
  serviceFeeCents,
  shouldShowDonation,
  skipOrderPricing,
  smallCartFeeCents,
  taxCents,
}: LegacyCartDisplayDiagnosticContributorProps) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { region } = useLocale();

  useDiagnosticContributor('cart-display-legacy', () => {
    const currency = String(region).toUpperCase() === 'CA' ? 'CAD' : 'USD';
    const itemTotal = cartEntries.reduce((total, entry) => total + priceForCartEntry(entry), 0);
    const row = (
      kind:
        | 'item_total_sum'
        | 'subtotal'
        | 'discount'
        | 'delivery_fee'
        | 'service_fee'
        | 'small_cart_fee'
        | 'geographical_fee'
        | 'fee'
        | 'tax'
        | 'total',
      label: string,
      minorAmount: number
    ) => {
      const availability =
        isLoading && !skipOrderPricing ? ('loading' as const) : ('available' as const);
      return {
        kind,
        label,
        money: diagnosticMoney({
          minorAmount,
          currency,
          formatted:
            availability === 'available' ? formatCurrencyForLocale(minorAmount) : undefined,
          source: skipOrderPricing ? ('local_cart' as const) : ('pricing_backend' as const),
          availability,
        }),
      };
    };
    const rows = [
      row('item_total_sum', 'Item total', itemTotal),
      ...(shouldShowDonation ? [row('fee', t('donation'), donationAmount)] : []),
      ...(rewardDiscount ? [row('discount', t('rewardSavings'), -rewardDiscount)] : []),
      ...(otherDiscountAmount
        ? [row('discount', t('comboOfferSavings'), -otherDiscountAmount)]
        : []),
      row('subtotal', t('subtotal'), displayedSubtotal),
      ...Object.entries(getFeesByGroup(fees ?? []) ?? {}).map(([name, amount]) =>
        row('fee', t(`${getStandardizedName({ name })}Fee`, name), amount)
      ),
    ];
    if (!skipOrderPricing && isDeliveryOrder) {
      rows.push(
        row(
          'delivery_fee',
          t('deliveryFee'),
          computeDeliveryFee({
            feeCents: deliveryFee,
            feeDiscountCents: deliveryFeeDiscount,
            serviceFeeCents,
            smallCartFeeCents,
            geographicalFeeCents,
          })
        )
      );
      if (geographicalFeeCents) {
        const geographicalFeeLabelId = isMappedGeographicalFeeState(dropoffState)
          ? geographicalFeeLabelIdMap[dropoffState]
          : 'geographicalFee';
        rows.push(row('geographical_fee', t(geographicalFeeLabelId), geographicalFeeCents));
      }
      if (serviceFeeCents) {
        rows.push(row('service_fee', t('serviceFee'), serviceFeeCents));
      }
      if (smallCartFeeCents) {
        rows.push(row('small_cart_fee', t('smallCartFee'), smallCartFeeCents));
      }
    }
    if (!skipOrderPricing) {
      rows.push(row('tax', t('tax'), taxCents));
    }
    rows.push(row('total', t('total'), displayedTotal));
    return {
      cart: {
        effectiveImplementation: 'legacy' as const,
        displayedSummaryRows: rows,
      },
    };
  });

  return null;
};

export default React.memo(Totals);

const styles = StyleSheet.create({
  deliveryLogoContainer: {
    alignSelf: 'center',
    width: spacing.$40,
  },
  donationRow: {
    marginBottom: spacing.$0,
  },
  wrapper: {
    marginVertical: spacing.$8,
    width: '100%',
  },
});
